import React, { FC, useContext, useState } from 'react';
import { Loader, Page, PageTitleWithButton } from '../../../components';
import { UserDetails } from './user-details/user-details';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { Preview } from '@mui/icons-material';
import { IAdminUser } from '../../../models';
import { UserContext } from '../../../context';
import { useSnackbar } from 'notistack';
import { getUser, impersonateEntraUser } from '../../../fetch';
import { useQuery, useQueryClient } from 'react-query';

interface IAddEditUser {}

export const AddEditUser: FC<IAddEditUser> = () => {
  const { userId } = useParams<{ userId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { isLoading: isLoadingUser, data: user } = useQuery<IAdminUser, Error>(
    ['get-user', userId],
    () => getUser(userId),
    {
      enabled: userId !== 'add-new-user'
    }
  );

  const { loadUser, canImpersonateExternalUser, canImpersonateInternalUser } =
    useContext(UserContext);
  const [isImpersonating, setImpersonating] = useState<boolean>(false);
  const canImpersonateUser =
    // user needs to be existing, so only on the edit page and either an internal/external user
    // also the logged in user needs the right roles to impersonate
    // you can't impersonate a global admin
    (!!user?.internalUser && canImpersonateInternalUser && user?.jobTitle !== 21 /*GlobalAdmin*/) ||
    (!!user?.externalUser && canImpersonateExternalUser);
  const displayName =
    user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.displayName;

  return (
    <Page
      title="User Details"
      customBreadcrumbPath={
        displayName ? `/admin/user-tools/unified-user-management/${displayName}` : ''
      }
    >
      <PageTitleWithButton
        pageTitleBackProps={{
          title: isLoadingUser
            ? 'Loading...'
            : userId === 'add-new-user'
            ? 'Add New User'
            : displayName
            ? `User Details - ${displayName}`
            : '',
          backText: 'Back to users',
          backUrl: '/admin/user-tools/unified-user-management',
          showDivider: false
        }}
        button={
          canImpersonateUser && (
            <Box flexWrap="wrap" display="flex" alignItems="center">
              <Button
                startIcon={<Preview />}
                disabled={isImpersonating}
                variant="contained"
                color="secondary"
                onClick={async () => {
                  try {
                    setImpersonating(true);
                    await impersonateEntraUser(user.entraUserId);
                    loadUser();
                    queryClient.invalidateQueries(['getAvailableNavigation']);
                    enqueueSnackbar('Impersonation Successful! Redirecting...', {
                      variant: 'success'
                    });

                    history.push('/');
                  } catch (error) {
                    return enqueueSnackbar(error?.Detail ?? 'Error impersonating user.', {
                      variant: 'error'
                    });
                  } finally {
                    setImpersonating(false);
                  }
                }}
              >
                Impersonate
              </Button>
            </Box>
          )
        }
      />
      {isLoadingUser ? (
        <Loader position="centered" />
      ) : (
        <UserDetails userId={userId} isLoadingUserDetails={false} currentUser={user} />
      )}
    </Page>
  );
};
